import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { replaceModal, pushModal } from 'loot-core/src/client/actions';
import * as queries from 'loot-core/client/queries';
import { openAccountCloseModal } from 'loot-core/src/client/actions';
import { reopenAccount } from 'loot-core/client/queries/queriesSlice';
import { type AccountEntity } from 'loot-core/types/models';
import { ChevronRight, Layers, Plane, Plus, UserRound, Wallet } from 'lucide-react';

import { useClosedAccounts } from '../../hooks/useClosedAccounts';
import { useLocalPref } from '../../hooks/useLocalPref';
import { useOffBudgetAccounts } from '../../hooks/useOffBudgetAccounts';
import { useOnBudgetAccounts } from '../../hooks/useOnBudgetAccounts';
import { useTravelAccounts } from '../../hooks/useTravelAccounts';
import { useDispatch } from '../../redux';
import { cn } from '../../lib/utils';
import { useFormat } from '../spreadsheet/useFormat';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import {
  SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu,
  SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '../ui/sidebar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { CellValue } from '../spreadsheet/CellValue';

function AccountBalance({ query }: { query: Parameters<typeof CellValue>[0]['binding'] }) {
  const format = useFormat();
  return (
    <CellValue binding={query} type="financial">
      {({ value }) => (
        <span className={cn('ml-auto text-xs tabular-nums', typeof value === 'number' && value < 0 ? 'text-destructive' : 'text-muted-foreground')}>
          {value != null ? format(value, 'financial') : null}
        </span>
      )}
    </CellValue>
  );
}

/** Zero-height drop indicator line — visually overlaid, never intercepts drag events. */
function DropLine({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="relative h-0 pointer-events-none">
      <div className="absolute inset-x-2 -top-px h-0.5 rounded-full bg-primary" />
    </div>
  );
}

function AccountItem({
  account, onDragStart, onDragOver, onDrop, onDragEnd, isDropAfter, isDragging,
}: {
  account: AccountEntity;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (aboveMidpoint: boolean) => void;
  onDrop: () => void;
  onDragEnd: () => void;
  isDropAfter: boolean;
  isDragging: boolean;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <SidebarMenuSubItem
        draggable
        onDragStart={e => { e.stopPropagation(); onDragStart(e); }}
        onDragOver={e => {
          e.preventDefault(); e.stopPropagation();
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          onDragOver(e.clientY < r.top + r.height / 2);
        }}
        onDrop={e => { e.stopPropagation(); onDrop(); }}
        onDragEnd={e => { e.stopPropagation(); onDragEnd(); }}
        className={cn('transition-opacity duration-150', isDragging && 'opacity-40')}
      >
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild><span className="sr-only" /></DropdownMenuTrigger>
          <SidebarMenuSubButton asChild>
            <NavLink
              to={`/accounts/${account.id}`}
              onContextMenu={e => { e.preventDefault(); setMenuOpen(true); }}
              className={({ isActive }) => cn(isActive && 'bg-sidebar-accent font-medium text-sidebar-accent-foreground')}
            >
              <span className="flex-1 truncate">{account.name}</span>
              <AccountBalance query={queries.accountBalance(account)} />
            </NavLink>
          </SidebarMenuSubButton>
          <DropdownMenuContent align="start" side="right" sideOffset={4}>
            <DropdownMenuItem onSelect={() => dispatch(pushModal('edit-local-account', { account }))}>
              {t('Edit account')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {account.closed ? (
              <DropdownMenuItem onSelect={() => dispatch(reopenAccount({ id: account.id }))}>
                {t('Reopen account')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => dispatch(openAccountCloseModal(account.id))}>
                {t('Close account')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuSubItem>
      <DropLine show={isDropAfter} />
    </>
  );
}

type AccountGroupProps = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  accounts: AccountEntity[];
  summaryQuery: Parameters<typeof CellValue>[0]['binding'];
  summaryTo: string;
  defaultOpen?: boolean;
};

function AccountGroup({ label, icon: Icon, accounts, summaryQuery, summaryTo, defaultOpen = true }: AccountGroupProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [ordered, setOrdered] = useState(accounts);
  const fromIdx = useRef<number | null>(null);
  const toIdx = useRef<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  const prevAccounts = useRef(accounts);
  if (prevAccounts.current !== accounts) { prevAccounts.current = accounts; setOrdered(accounts); }

  const startDrag = (index: number, e: React.DragEvent) => {
    fromIdx.current = index;
    setDraggingIdx(index);
    const ghost = document.createElement('div');
    ghost.style.cssText = 'position:fixed;top:-9999px;width:1px;height:1px';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    requestAnimationFrame(() => document.body.removeChild(ghost));
  };

  const over = (index: number, aboveMid: boolean) => {
    const insertAfter = aboveMid ? index - 1 : index;
    toIdx.current = insertAfter;
    setOverIdx(insertAfter);
  };

  const reset = () => { fromIdx.current = null; toIdx.current = null; setOverIdx(null); setDraggingIdx(null); };

  const drop = () => {
    const from = fromIdx.current, to = toIdx.current;
    if (from !== null && to !== null) {
      const insertPos = to + 1;
      const next = [...ordered];
      const [moved] = next.splice(from, 1);
      const at = Math.min(from < insertPos ? insertPos - 1 : insertPos, next.length);
      if (at !== from) { next.splice(at, 0, moved); setOrdered(next); }
    }
    reset();
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group/collapsible">
      <SidebarMenuItem>
        <div className="flex items-center w-full rounded-md hover:bg-sidebar-accent group/header">
          <SidebarMenuButton asChild className="flex-1 font-medium text-xs uppercase tracking-wider text-muted-foreground group-hover/header:text-sidebar-foreground bg-transparent hover:bg-transparent p-0 pl-2">
            <NavLink to={summaryTo} className="flex items-center gap-2 w-full h-full py-1.5">
              <Icon className="size-3.5 shrink-0" />
              <span className="flex-1 truncate">{label}</span>
            </NavLink>
          </SidebarMenuButton>
          <CollapsibleTrigger className="flex items-center justify-center p-1.5 pr-2 text-muted-foreground group-hover/header:text-sidebar-foreground shrink-0">
            <ChevronRight className="size-3 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <SidebarMenuSub>
            <DropLine show={draggingIdx !== null && overIdx === -1} />
            {ordered.map((account, index) => (
              <AccountItem
                key={account.id}
                account={account}
                onDragStart={e => startDrag(index, e)}
                onDragOver={aboveMid => over(index, aboveMid)}
                onDrop={drop}
                onDragEnd={reset}
                isDropAfter={draggingIdx !== null && overIdx === index}
                isDragging={draggingIdx === index}
              />
            ))}
            {/* Trailing zone so dragging below the last item is catchable */}
            {draggingIdx !== null && (
              <div className="h-4"
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); over(ordered.length - 1, false); }}
                onDrop={e => { e.stopPropagation(); drop(); }}
              />
            )}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

export function AccountsList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const onBudgetAccounts = useOnBudgetAccounts();
  const offBudgetAccounts = useOffBudgetAccounts();
  const travelAccounts = useTravelAccounts();
  const closedAccounts = useClosedAccounts();
  const [showClosed, setShowClosed] = useLocalPref('ui.showClosedAccounts');

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{t('Accounts')}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <NavLink to="/accounts" end className={({ isActive }) => cn('font-medium', isActive && 'bg-sidebar-accent text-sidebar-accent-foreground')}>
                <Wallet className="size-4 shrink-0" />
                <span className="flex-1 truncate">{t('All accounts')}</span>
                <AccountBalance query={queries.allAccountBalance()} />
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {onBudgetAccounts.length > 0 && <AccountGroup label={t('On budget')} icon={Layers} accounts={onBudgetAccounts} summaryQuery={queries.onBudgetAccountBalance()} summaryTo="/accounts/onbudget" />}
          {offBudgetAccounts.length > 0 && <AccountGroup label={t('Off budget')} icon={UserRound} accounts={offBudgetAccounts} summaryQuery={queries.offBudgetAccountBalance()} summaryTo="/accounts/offbudget" />}
          {travelAccounts.length > 0 && <AccountGroup label={t('Travel')} icon={Plane} accounts={travelAccounts} summaryQuery={queries.travelAccountBalance()} summaryTo="/accounts/travel" />}

          {closedAccounts.length > 0 && (
            <>
              <SidebarMenuItem>
                <SidebarMenuButton className="text-xs text-muted-foreground" onClick={() => setShowClosed(!showClosed)}>
                  {showClosed ? t('Hide closed accounts') : t('Show closed accounts')}
                </SidebarMenuButton>
              </SidebarMenuItem>
              {showClosed && <AccountGroup label={t('Closed')} icon={Layers} accounts={closedAccounts} summaryQuery={queries.allAccountBalance()} summaryTo="/accounts" defaultOpen={true} />}
            </>
          )}

          <SidebarMenuItem>
            <SidebarMenuButton className="text-muted-foreground hover:text-sidebar-foreground" onClick={() => dispatch(replaceModal('add-account'))}>
              <Plus className="size-4" />
              <span>{t('Add account')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
